import {
  Container,
  Box,
  Typography,
  LinearProgress,
  Alert,
  TablePagination,
  IconButton,
  CircularProgress,
} from '@mui/material';
import PreviewIcon from '@mui/icons-material/Preview';
import { SetStateAction, useContext, useEffect, useState } from 'react';
import { DoubleDate } from '../../common/double-date-picker/double-date-picker.interfaces';
import DoubleDatePicker from '../../common/double-date-picker/double-date-picker';
import { AuthContext } from '../../../contexts/user-context/user-context';
import { API_ENDPOINT } from '../../../configurations/global.config';
import axios, { AxiosResponse } from 'axios';
import { ErrorsFlattenner } from '../../common/errors-flattenner/errors-flattenner';
import { PaginationContext } from '../../../contexts/pagination-context';
import {
  DriverInvoiceState,
  DriverInvoiceType,
  IDriverInvoice,
} from '../../../interfaces/common/driver-invoices';
import { IFilterOption } from '../../common/filter-bar/types';
import GenericTable from '../../common/generic-table/generic-table';
import { displayMoney } from '../../../utils/money';
import { showDriverInvoiceState } from '../../../utils/driver-invoices';
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining';
import InvoicePDFDialog from '../../common/invoice-pdf-dialog/invoice-pdf-dialog';
import ResponsiveInvoicesListing from '../common/driver-invoices-listing';
import PendingCashOperation from './pending-cash-operation';

function DriverInvoices() {
  const [invoices, setInvoices] = useState<IDriverInvoice[]>([]);
  const [invoicesCount, setInvoicesCount] = useState<number>(0);
  const [response, setResponse] = useState<AxiosResponse>();
  const [message, setMessage] = useState<string>('');
  const [filters, setFilters] = useState<IFilterOption[]>([]);

  const [lockedInvoiceMeta, setLockedInvoiceMeta] =
    useState<IDriverInvoice | null>(null);
  const [lockType, setLockType] = useState<'view' | 'delete' | null>(null);

  const [pdfData, setPdfData] = useState<IDriverInvoice | null>(null);

  const [dateFilter, setDateFilter] = useState<DoubleDate>({
    startDate: null,
    endDate: null,
  });

  const [isLoading, setIsLoading] = useState(false);

  const { user, config } = useContext(AuthContext);

  const { paginationConfig, setPaginationConfig } =
    useContext(PaginationContext);
  const [paginationController, setPaginationController] = useState({
    page: 0,
    rowsPerPage: paginationConfig.rowsPerPage,
  });

  useEffect(() => {
    let response: any;
    const orgId = user?.organisation?.org_id;
    if (orgId === undefined) return;
    const getData = async () => {
      let endpoint = `${API_ENDPOINT}/api/v1/driver-invoices?page=${paginationController.page + 1}&page_size=${paginationController.rowsPerPage}&org=${encodeURIComponent(orgId)}`;

      const { startDate, endDate } = dateFilter;

      if (startDate !== null) {
        endpoint += `&date_from=${encodeURIComponent(startDate.format('YYYY-MM-DD'))}`;
      }

      if (endDate !== null) {
        endpoint += `&date_to=${encodeURIComponent(endDate.format('YYYY-MM-DD'))}`;
      }

      console.log(endpoint);
      try {
        response = await axios.get<{
          count: number;
          results: IDriverInvoice[];
        }>(endpoint, config);
        setInvoices(response.data.results);
        setInvoicesCount(response.data.count);
        console.log(response);
      } catch (error) {
        console.log(error);
      }
      setIsLoading(false);
    };
    setIsLoading(true);
    getData();
    console.log(response);
  }, [dateFilter, paginationController]);

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ) => {
    setPaginationController({
      ...paginationController,
      page: newPage,
    });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setPaginationConfig({ ...paginationConfig, rowsPerPage: newRowsPerPage });
    setPaginationController({
      ...paginationController,
      rowsPerPage: newRowsPerPage,
      page: 0,
    });
  };

  const resetPaginationController = () => {
    setPaginationController({
      ...paginationController,
      page: 0,
    });
  };

  const dateFilterAction = (dateFilterValue: DoubleDate) => {
    resetPaginationController();
    setDateFilter(dateFilterValue);
  };

  const fetchInvoiceContent = async (invoicemeta: IDriverInvoice) => {
    setLockedInvoiceMeta(invoicemeta);
    setLockType('view');

    const endpoint = `${API_ENDPOINT}/api/v1/driver-invoice/${invoicemeta.id}/`;

    try {
      const response = await axios.get(endpoint, config);
      setResponse(response);
      console.log('target = ', response);
      const data: IDriverInvoice = response.data;
      setPdfData(data);
    } catch (e) {
      if (axios.isAxiosError(e)) {
        setResponse(e.response);
        setMessage(
          'Failed to download invoice content: ' + e.response?.statusText
        );
      } else {
        setMessage('Failed to download invoice content');
      }
    }
    setLockedInvoiceMeta(null);
    setLockType(null);
  };

  return (
    <div>
      <Box>
        <Typography
          variant="h3"
          component="h3"
          sx={{ textAlign: 'center', mt: 3, mb: 3 }}
        >
          Invoices
        </Typography>
        <br />
        <PendingCashOperation />
        <br />
        <DoubleDatePicker dateFilterAction={dateFilterAction} />
        <br />

        {message !== '' && (
          <>
            <Alert
              severity={
                response && response.status >= 200 && response.status < 300
                  ? 'success'
                  : 'error'
              }
            >
              <>
                {message}

                {response && response.status >= 300 && (
                  <ErrorsFlattenner rawErrors={response.data} />
                )}
              </>
            </Alert>

            <br />
          </>
        )}

        <ResponsiveInvoicesListing
          invoicesList={invoices}
          isLoading={isLoading}
          setLockedInvoiceMeta={setLockedInvoiceMeta}
          setLockType={setLockType}
          fetchInvoiceContent={fetchInvoiceContent}
          lockedInvoiceMeta={lockedInvoiceMeta}
          lockType={lockType}
        />
        <TablePagination
          component="div"
          onPageChange={handlePageChange}
          page={paginationController.page}
          count={invoicesCount}
          rowsPerPage={paginationController.rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50, 100, 200, 250, 500, 1000]}
          labelRowsPerPage=""
          style={{ overflow: 'visible' }}
        />
      </Box>

      {pdfData && pdfData.driver && (
        <InvoicePDFDialog
          pdfData={pdfData}
          onClose={() => setPdfData(null)}
          driver={pdfData.driver}
        />
      )}
    </div>
  );
}

export default DriverInvoices;
